import { formatEventDate } from "../utils/formatEventDate"
import MasonryCollection from "./components/MasonryCollection"
import { GalleryDesignProps } from "./types"
import PhotographerBar from "./components/PhotographerBar"
import CoverImage from "./components/CoverImage"

/**
 * Occasion · Corporate
 * Light + dark. A structured, professional cover: a solid navy info panel
 * (uppercase Space Grotesk kicker, bold event name, accent rule, date) beside a
 * full-height cover photo. Crisp slate / navy / accent-blue palette. Photos use
 * the shared Masonry collection. Color mode from the body `dark` class.
 */
const CorporateGallery = ({
  data,
  folderBar,
  contentMode,
  aiContent,
  emptyContent,
  photos,
  onCoverLoad,
}: GalleryDesignProps) => {
  const name = data?.name || ""
  const dateLabel = formatEventDate(data?.startDate, data?.endDate, data?.date)
  const cover = data?.coverPhotoLink
  const vendor = data?.vendors?.[0]

  return (
    <div className="relative w-full bg-[#f4f6f8] text-[#101826] dark:bg-[#0b1017] dark:text-[#e7edf5] font-archivo">
      {/* Split cover: info panel + photo. DOM order is [panel, image]; on mobile
          flex-col-reverse puts the photo on top, the panel below. */}
      <div className="relative w-full flex flex-col-reverse lg:flex-row lg:h-screen">
        <PhotographerBar vendor={vendor} tone="light" />

        {/* Solid navy info panel */}
        <div className="w-full lg:w-[42%] bg-[#102a4c] text-white flex flex-col justify-center px-8 md:px-14 py-10 md:py-0">
          <p className="font-spacegrotesk text-[11px] md:text-[12px] font-medium tracking-[0.32em] uppercase text-[#6ea0ff]">
            Event Gallery
          </p>
          <h1 className="font-spacegrotesk font-bold leading-[1.04] tracking-[-0.01em] text-[30px] md:text-[46px] lg:text-[52px] mt-4 break-words">
            {name}
          </h1>
          <span className="block h-[3px] w-14 bg-[#2f6fed] mt-6 mb-5" />
          {dateLabel && (
            <p className="font-spacegrotesk text-sm md:text-base tracking-[0.04em] text-white/80">
              {dateLabel}
            </p>
          )}
        </div>

        {/* Cover photo */}
        <div className="w-full aspect-[16/10] lg:aspect-auto lg:flex-1 lg:h-full bg-[#dfe4ea] dark:bg-[#151c26] overflow-hidden">
          {cover && (
            <CoverImage
              src={cover}
              alt={name}
              className="w-full h-full object-cover"
              onLoad={onCoverLoad}
            />
          )}
        </div>
      </div>

      <div className="flex flex-col items-center w-full">
        {folderBar}

        <div className="w-full px-4 md:px-12 pt-6 pb-10 min-h-[40vh]">
          {contentMode === "ai" ? (
            aiContent
          ) : contentMode === "photos" ? (
            <MasonryCollection photos={photos} columnsClass="columns-2 md:columns-3" />
          ) : (
            emptyContent
          )}
        </div>
      </div>
    </div>
  )
}

export default CorporateGallery
